/**
 * AvailabilitySettingsModal
 *
 * Modal para configurar disponibilidade de calendário do usuário.
 * Chrome via Modal/ModalHeader/ModalFooter shared (F72), título 18 (F74),
 * abas via Tabs shared (F56), controles via Input/Select/Checkbox shared.
 */

import React, { useState, useEffect } from 'react';
import type { CalendarNotificationPreferences, UserCalendarPreferences } from '@troia-v3/types';
import { useUserCalendarPreferences } from '../hooks';
import {
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  Tabs,
  Input,
  Select,
  Checkbox,
  type TabItem,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

interface AvailabilitySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: UserCalendarPreferences) => Promise<void>;
}

type AvailabilityTab = 'general' | 'hours' | 'breaks' | 'blocked';

export const AvailabilitySettingsModal: React.FC<AvailabilitySettingsModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { data: initialPreferences, isLoading } = useUserCalendarPreferences();
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<AvailabilityTab>('general');

  // Notificações de evento (#132) — aba "Geral"
  const [eventNotifications, setEventNotifications] = useState<CalendarNotificationPreferences>({
    reminderDefault: { enabled: true, minutes: 15 },
    emailInvite: { enabled: true },
    dailySummary: { enabled: false },
  });

  // Estado do formulário
  const [workingHours, setWorkingHours] = useState({
    monday: { enabled: true, start: '09:00', end: '18:00' },
    tuesday: { enabled: true, start: '09:00', end: '18:00' },
    wednesday: { enabled: true, start: '09:00', end: '18:00' },
    thursday: { enabled: true, start: '09:00', end: '18:00' },
    friday: { enabled: true, start: '09:00', end: '18:00' },
    saturday: { enabled: false, start: '09:00', end: '18:00' },
    sunday: { enabled: false, start: '09:00', end: '18:00' }
  });

  const [breaks, setBreaks] = useState([
    {
      name: 'Almoço',
      start: '12:00',
      end: '13:00',
      daysOfWeek: [1, 2, 3, 4, 5],
      enabled: true
    }
  ]);

  const [blockedDates, setBlockedDates] = useState<Array<{
    startDate: string;
    endDate: string;
    reason?: string;
    allDay: boolean;
    startTime?: string;
    endTime?: string;
  }>>([]);

  const [holidays, setHolidays] = useState<{
    enabled: boolean;
    country: string;
    region?: string;
    customHolidays: Array<{ date: string; name: string; recurring: boolean }>;
  }>({
    enabled: false,
    country: 'BR',
    customHolidays: []
  });

  const [defaultMeetingDuration, setDefaultMeetingDuration] = useState(60);
  const [meetingBuffer, setMeetingBuffer] = useState({ enabled: false, minutes: 0 });

  // Inicializar com dados existentes
  useEffect(() => {
    if (initialPreferences) {
      setWorkingHours(initialPreferences.workingHours);
      setBreaks(initialPreferences.breaks);
      setBlockedDates(initialPreferences.blockedDates || []);
      setHolidays(initialPreferences.holidays);
      setDefaultMeetingDuration(initialPreferences.defaultMeetingDuration);
      setMeetingBuffer(initialPreferences.meetingBuffer);
      if (initialPreferences.notifications) {
        setEventNotifications({
          reminderDefault: initialPreferences.notifications.reminderDefault ?? { enabled: true, minutes: 15 },
          emailInvite: initialPreferences.notifications.emailInvite ?? { enabled: true },
          dailySummary: initialPreferences.notifications.dailySummary ?? { enabled: false },
        });
      }
    }
  }, [initialPreferences]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const preferences: UserCalendarPreferences = {
        workingHours,
        breaks,
        blockedDates,
        holidays,
        meetingBuffer,
        defaultMeetingDuration,
        dailyMeetingLimit: initialPreferences?.dailyMeetingLimit,
        notifications: eventNotifications
      };

      await onSave(preferences);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const dayNames = {
    monday: 'Segunda-feira',
    tuesday: 'Terça-feira',
    wednesday: 'Quarta-feira',
    thursday: 'Quinta-feira',
    friday: 'Sexta-feira',
    saturday: 'Sábado',
    sunday: 'Domingo'
  };

  const handleAddBreak = () => {
    setBreaks([
      ...breaks,
      {
        name: '',
        start: '12:00',
        end: '13:00',
        daysOfWeek: [1, 2, 3, 4, 5],
        enabled: true
      }
    ]);
  };

  const handleRemoveBreak = (index: number) => {
    setBreaks(breaks.filter((_, i) => i !== index));
  };

  const handleAddBlockedDate = () => {
    const today = new Date().toISOString().split('T')[0];
    setBlockedDates([
      ...blockedDates,
      {
        startDate: today,
        endDate: today,
        reason: '',
        allDay: true,
        startTime: '09:00',
        endTime: '18:00'
      }
    ]);
  };

  const handleRemoveBlockedDate = (index: number) => {
    setBlockedDates(blockedDates.filter((_, i) => i !== index));
  };

  const handleAddCustomHoliday = () => {
    setHolidays({
      ...holidays,
      customHolidays: [
        ...holidays.customHolidays,
        {
          date: new Date().toISOString().split('T')[0],
          name: '',
          recurring: false
        }
      ]
    });
  };

  const handleRemoveCustomHoliday = (index: number) => {
    setHolidays({
      ...holidays,
      customHolidays: holidays.customHolidays.filter((_, i) => i !== index)
    });
  };

  // ── Painéis das abas (conteúdo do Tabs shared) ──────────────────────────

  const generalPanel = (
    <div className="p-4 space-y-6" data-testid="availability-tab-general-panel">
      <div>
        <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
          Notificações de evento
        </h3>
        <p className="text-xs mb-3" style={{ color: 'var(--theme-muted-text)' }}>
          Escolha como você quer ser avisado sobre seus eventos.
        </p>
        <div className="space-y-2">
          <div
            className="p-4 rounded-lg border"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
          >
            <Checkbox
              label="Lembrete padrão"
              description="Notificar 15 min antes de cada evento (quando o evento não tem lembrete próprio)"
              checked={eventNotifications.reminderDefault.enabled}
              onChange={(checked) =>
                setEventNotifications((prev) => ({
                  ...prev,
                  reminderDefault: { ...prev.reminderDefault, enabled: checked }
                }))
              }
              data-testid="availability-notif-reminder"
            />
          </div>

          <div
            className="p-4 rounded-lg border"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
          >
            <Checkbox
              label="Convite por e-mail"
              description="Enviar convite aos participantes ao criar o evento"
              checked={eventNotifications.emailInvite.enabled}
              onChange={(checked) =>
                setEventNotifications((prev) => ({
                  ...prev,
                  emailInvite: { enabled: checked }
                }))
              }
              data-testid="availability-notif-invite"
            />
          </div>

          <div
            className="p-4 rounded-lg border"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
          >
            <Checkbox
              label="Resumo diário"
              description="Receber a agenda do dia às 08:00"
              checked={eventNotifications.dailySummary.enabled}
              onChange={(checked) =>
                setEventNotifications((prev) => ({
                  ...prev,
                  dailySummary: { enabled: checked }
                }))
              }
              data-testid="availability-notif-summary"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const hoursPanel = (
    <div className="p-4 space-y-4" data-testid="availability-tab-hours-panel">
      {Object.entries(workingHours).map(([day, config]) => (
        <div
          key={day}
          className="flex items-center gap-4 p-4 rounded-lg border"
          style={{
            backgroundColor: config.enabled ? 'var(--theme-card-background)' : 'var(--theme-button-ghost-hover)',
            borderColor: 'var(--theme-card-border)'
          }}
        >
          <Checkbox
            checked={config.enabled}
            onChange={(checked) => {
              setWorkingHours({
                ...workingHours,
                [day]: { ...config, enabled: checked }
              });
            }}
            data-testid={`availability-hours-enabled-${day}`}
          />

          {/* Nome do dia */}
          <div className="w-32">
            <span
              className="text-sm font-medium"
              style={{ color: config.enabled ? 'var(--theme-text)' : 'var(--theme-muted-text)' }}
            >
              {dayNames[day as keyof typeof dayNames]}
            </span>
          </div>

          {/* Horários */}
          <div className="flex items-center gap-2 flex-1">
            <Input
              type="time"
              value={config.start}
              disabled={!config.enabled}
              fullWidth={false}
              onChange={(e) => {
                setWorkingHours({
                  ...workingHours,
                  [day]: { ...config, start: e.target.value }
                });
              }}
              data-testid={`availability-hours-start-${day}`}
            />
            <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>até</span>
            <Input
              type="time"
              value={config.end}
              disabled={!config.enabled}
              fullWidth={false}
              onChange={(e) => {
                setWorkingHours({
                  ...workingHours,
                  [day]: { ...config, end: e.target.value }
                });
              }}
              data-testid={`availability-hours-end-${day}`}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const breaksPanel = (
    <div className="p-4 space-y-6" data-testid="availability-tab-breaks-panel">
      {/* Duração Padrão de Reunião */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          Duração padrão de reuniões (minutos)
        </label>
        <Input
          type="number"
          value={defaultMeetingDuration}
          onChange={(e) => setDefaultMeetingDuration(parseInt(e.target.value))}
          min={15}
          step={15}
          data-testid="availability-meeting-duration"
        />
      </div>

      {/* Buffer entre reuniões */}
      <div>
        <div className="mb-2">
          <Checkbox
            label="Tempo de buffer entre reuniões"
            checked={meetingBuffer.enabled}
            onChange={(checked) => setMeetingBuffer({ ...meetingBuffer, enabled: checked })}
            data-testid="availability-buffer-enabled"
          />
        </div>
        {meetingBuffer.enabled && (
          <Input
            type="number"
            value={meetingBuffer.minutes}
            onChange={(e) => setMeetingBuffer({ ...meetingBuffer, minutes: parseInt(e.target.value) })}
            min={0}
            step={5}
            placeholder="Minutos de buffer"
            data-testid="availability-buffer-minutes"
          />
        )}
      </div>

      {/* Pausas */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
            Pausas
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="plus"
            onClick={handleAddBreak}
            className="!text-[var(--theme-primary)]"
            data-testid="availability-add-break"
          >
            Adicionar Pausa
          </Button>
        </div>
        <div className="space-y-3">
          {breaks.map((breakItem, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border relative"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)'
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  checked={breakItem.enabled}
                  onChange={(checked) => {
                    const newBreaks = [...breaks];
                    newBreaks[index].enabled = checked;
                    setBreaks(newBreaks);
                  }}
                  data-testid={`availability-break-enabled-${index}`}
                />
                <Input
                  type="text"
                  value={breakItem.name}
                  onChange={(e) => {
                    const newBreaks = [...breaks];
                    newBreaks[index].name = e.target.value;
                    setBreaks(newBreaks);
                  }}
                  placeholder="Nome da pausa"
                  data-testid={`availability-break-name-${index}`}
                />
                {breaks.length > 1 && (
                  <button
                    onClick={() => handleRemoveBreak(index)}
                    className="p-1 rounded transition-colors flex-none"
                    style={{ color: 'var(--theme-error-color)' }}
                    title="Remover pausa"
                    data-testid={`availability-break-remove-${index}`}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={breakItem.start}
                  fullWidth={false}
                  onChange={(e) => {
                    const newBreaks = [...breaks];
                    newBreaks[index].start = e.target.value;
                    setBreaks(newBreaks);
                  }}
                  data-testid={`availability-break-start-${index}`}
                />
                <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>até</span>
                <Input
                  type="time"
                  value={breakItem.end}
                  fullWidth={false}
                  onChange={(e) => {
                    const newBreaks = [...breaks];
                    newBreaks[index].end = e.target.value;
                    setBreaks(newBreaks);
                  }}
                  data-testid={`availability-break-end-${index}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const blockedPanel = (
    <div className="p-4 space-y-6" data-testid="availability-tab-blocked-panel">
      {/* Datas Bloqueadas */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
            Datas Bloqueadas
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="plus"
            onClick={handleAddBlockedDate}
            className="!text-[var(--theme-primary)]"
            data-testid="availability-add-blocked-date"
          >
            Adicionar Data
          </Button>
        </div>
        <div className="space-y-3">
          {blockedDates.length === 0 ? (
            <div
              className="p-4 rounded-lg border text-center"
              style={{
                backgroundColor: 'var(--theme-button-ghost-hover)',
                borderColor: 'var(--theme-card-border)'
              }}
            >
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhuma data bloqueada
              </p>
            </div>
          ) : (
            blockedDates.map((blockedDate, index) => (
              <div
                key={index}
                className="p-4 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-3">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Data Inicial
                      </label>
                      <Input
                        type="date"
                        value={blockedDate.startDate}
                        onChange={(e) => {
                          const newBlockedDates = [...blockedDates];
                          newBlockedDates[index].startDate = e.target.value;
                          setBlockedDates(newBlockedDates);
                        }}
                        data-testid={`availability-blocked-start-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Data Final
                      </label>
                      <Input
                        type="date"
                        value={blockedDate.endDate}
                        onChange={(e) => {
                          const newBlockedDates = [...blockedDates];
                          newBlockedDates[index].endDate = e.target.value;
                          setBlockedDates(newBlockedDates);
                        }}
                        data-testid={`availability-blocked-end-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Motivo (opcional)
                      </label>
                      <Input
                        type="text"
                        value={blockedDate.reason || ''}
                        onChange={(e) => {
                          const newBlockedDates = [...blockedDates];
                          newBlockedDates[index].reason = e.target.value;
                          setBlockedDates(newBlockedDates);
                        }}
                        placeholder="Ex: Férias, Feriado"
                        data-testid={`availability-blocked-reason-${index}`}
                      />
                    </div>
                    <Checkbox
                      label="Dia inteiro"
                      checked={blockedDate.allDay}
                      onChange={(checked) => {
                        const newBlockedDates = [...blockedDates];
                        newBlockedDates[index].allDay = checked;
                        setBlockedDates(newBlockedDates);
                      }}
                      data-testid={`availability-blocked-allday-${index}`}
                    />
                    {!blockedDate.allDay && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                            Hora Inicial
                          </label>
                          <Input
                            type="time"
                            value={blockedDate.startTime || '09:00'}
                            onChange={(e) => {
                              const newBlockedDates = [...blockedDates];
                              newBlockedDates[index].startTime = e.target.value;
                              setBlockedDates(newBlockedDates);
                            }}
                            data-testid={`availability-blocked-starttime-${index}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                            Hora Final
                          </label>
                          <Input
                            type="time"
                            value={blockedDate.endTime || '18:00'}
                            onChange={(e) => {
                              const newBlockedDates = [...blockedDates];
                              newBlockedDates[index].endTime = e.target.value;
                              setBlockedDates(newBlockedDates);
                            }}
                            data-testid={`availability-blocked-endtime-${index}`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveBlockedDate(index)}
                    className="p-1 rounded transition-colors mt-6"
                    style={{ color: 'var(--theme-error-color)' }}
                    title="Remover data bloqueada"
                    data-testid={`availability-blocked-remove-${index}`}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Feriados */}
      <div>
        <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--theme-text)' }}>
          Feriados Nacionais
        </h3>
        <div
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)'
          }}
        >
          <Checkbox
            label="Bloquear feriados automaticamente"
            checked={holidays.enabled}
            onChange={(checked) => setHolidays({ ...holidays, enabled: checked })}
            data-testid="availability-holidays-enabled"
          />
          {holidays.enabled && (
            <div className="space-y-3 mt-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                  País
                </label>
                <Select
                  value={holidays.country}
                  onChange={(value) => setHolidays({ ...holidays, country: value })}
                  options={[
                    { value: 'BR', label: 'Brasil' },
                    { value: 'US', label: 'Estados Unidos' },
                    { value: 'PT', label: 'Portugal' },
                  ]}
                  data-testid="availability-holidays-country"
                />
              </div>
              {holidays.country === 'BR' && (
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Estado (opcional)
                  </label>
                  <Input
                    type="text"
                    value={holidays.region || ''}
                    onChange={(e) => setHolidays({ ...holidays, region: e.target.value })}
                    placeholder="Ex: SP, RJ, MG"
                    data-testid="availability-holidays-region"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feriados Customizados */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
            Feriados Customizados
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="plus"
            onClick={handleAddCustomHoliday}
            className="!text-[var(--theme-primary)]"
            data-testid="availability-add-holiday"
          >
            Adicionar Feriado
          </Button>
        </div>
        <div className="space-y-3">
          {holidays.customHolidays.length === 0 ? (
            <div
              className="p-4 rounded-lg border text-center"
              style={{
                backgroundColor: 'var(--theme-button-ghost-hover)',
                borderColor: 'var(--theme-card-border)'
              }}
            >
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhum feriado customizado
              </p>
            </div>
          ) : (
            holidays.customHolidays.map((holiday, index) => (
              <div
                key={index}
                className="p-4 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                          Nome do Feriado
                        </label>
                        <Input
                          type="text"
                          value={holiday.name}
                          onChange={(e) => {
                            const newHolidays = { ...holidays };
                            newHolidays.customHolidays[index].name = e.target.value;
                            setHolidays(newHolidays);
                          }}
                          placeholder="Ex: Dia da Empresa"
                          data-testid={`availability-holiday-name-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                          Data
                        </label>
                        <Input
                          type="date"
                          value={holiday.date}
                          onChange={(e) => {
                            const newHolidays = { ...holidays };
                            newHolidays.customHolidays[index].date = e.target.value;
                            setHolidays(newHolidays);
                          }}
                          data-testid={`availability-holiday-date-${index}`}
                        />
                      </div>
                    </div>
                    <Checkbox
                      label="Repetir anualmente (ex: aniversário da empresa)"
                      checked={holiday.recurring}
                      onChange={(checked) => {
                        const newHolidays = { ...holidays };
                        newHolidays.customHolidays[index].recurring = checked;
                        setHolidays(newHolidays);
                      }}
                      data-testid={`availability-holiday-recurring-${index}`}
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveCustomHoliday(index)}
                    className="p-1 rounded transition-colors mt-6"
                    style={{ color: 'var(--theme-error-color)' }}
                    title="Remover feriado"
                    data-testid={`availability-holiday-remove-${index}`}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  const tabs: TabItem[] = [
    { id: 'general', label: 'Geral', content: generalPanel },
    { id: 'hours', label: 'Horários de Trabalho', content: hoursPanel },
    { id: 'breaks', label: 'Pausas e Preferências', content: breaksPanel },
    { id: 'blocked', label: 'Bloqueios e Feriados', content: blockedPanel },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="4xl">
      <ModalHeader title="Configurações de Disponibilidade" onClose={onClose} />

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--theme-primary)' }}
            />
            <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              Carregando preferências...
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col" style={{ maxHeight: '65vh' }} data-testid="availability-tabs">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={(tabId) => setActiveTab(tabId as AvailabilityTab)}
            className="px-4 overflow-x-auto"
          />
        </div>
      )}

      <ModalFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSaving}
          data-testid="availability-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          icon="save"
          loading={isSaving}
          disabled={isSaving}
          onClick={handleSave}
          data-testid="availability-save"
        >
          Salvar Configurações
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AvailabilitySettingsModal.displayName = 'AvailabilitySettingsModal';
